import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch } from 'lucide-react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { isBackendWorkspace } from '../utils/workspaceStorage';

function ForkField({ label, children, helper }) {
  return (
    <div className="space-y-1">
      {label ? (
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">{label}</label>
      ) : null}
      {children}
      {helper ? <p className="text-xs text-gray-500 dark:text-zinc-500">{helper}</p> : null}
    </div>
  );
}

function ForkSelectField({ label, helper, ...pickerProps }) {
  return (
    <ForkField label={label} helper={helper}>
      <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
        <CompactSelectPicker {...pickerProps} />
      </div>
    </ForkField>
  );
}

function ForkTextField({ label, ...inputProps }) {
  return (
    <ForkField label={label}>
      <input {...inputProps} className="modal-input" />
    </ForkField>
  );
}

export default function ForkWorkspaceModal({
  isOpen,
  onClose,
  sourceWorkspace,
  localWorkspaces = [],
  isAuthenticated = false,
  isOnline = true,
  onConfirm,
  loading = false,
}) {
  const { t } = useTranslation();
  const destinations = useMemo(
    () => [
      { id: 'personal', label: t('fork.destPersonal'), description: t('fork.destPersonalDesc') },
      { id: 'local', label: t('fork.destLocal'), description: t('fork.destLocalDesc') },
    ],
    [t]
  );
  const localTargets = useMemo(
    () => [
      { id: 'new', label: t('fork.localNew') },
      { id: 'existing', label: t('fork.localExisting') },
    ],
    [t]
  );
  const [destination, setDestination] = useState('personal');
  const [localTarget, setLocalTarget] = useState('new');
  const [existingLocalId, setExistingLocalId] = useState('');
  const [newLocalName, setNewLocalName] = useState('');
  const [copyVariables, setCopyVariables] = useState(true);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !sourceWorkspace) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    const copyVars = sourceWorkspace.settings?.fork_copy_variables !== false;
    setCopyVariables(copyVars);
    setNewLocalName(
      t('fork.workspaceForkSuffix', { name: sourceWorkspace.name || t('workspaceModal.createTitle') })
    );
    if (!isAuthenticated || !isOnline) {
      setDestination('local');
    } else if (isBackendWorkspace(sourceWorkspace, isAuthenticated)) {
      setDestination('personal');
    } else {
      setDestination('local');
    }
    setLocalTarget(localWorkspaces.length > 0 ? 'existing' : 'new');
    setExistingLocalId(localWorkspaces[0]?.id || '');
  }, [isOpen, sourceWorkspace, isAuthenticated, isOnline, localWorkspaces, t]);

  const cloudDisabled = !isAuthenticated || !isOnline;

  const destinationOptions = useMemo(
    () =>
      destinations
        .filter((d) => !(cloudDisabled && d.id === 'personal'))
        .map((d) => ({ value: d.id, label: d.label })),
    [cloudDisabled, destinations]
  );

  const destinationHelper = useMemo(() => {
    if (cloudDisabled) {
      return t('fork.signInForCloud');
    }
    return destinations.find((d) => d.id === destination)?.description ?? '';
  }, [cloudDisabled, destination, destinations, t]);

  const localTargetOptions = useMemo(() => {
    if (localWorkspaces.length === 0) {
      return localTargets.filter((opt) => opt.id === 'new').map((opt) => ({
        value: opt.id,
        label: opt.label,
      }));
    }
    return localTargets.map((opt) => ({ value: opt.id, label: opt.label }));
  }, [localWorkspaces.length, localTargets]);

  const existingLocalOptions = useMemo(
    () => localWorkspaces.map((w) => ({ value: String(w.id), label: w.name })),
    [localWorkspaces]
  );

  if (!isOpen || !sourceWorkspace) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm?.({
      destination,
      localTarget,
      existingLocalId: localTarget === 'existing' ? existingLocalId : null,
      newLocalName: localTarget === 'new' ? newLocalName.trim() : null,
      copyVariables,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('fork.title')} subtitle="Choose where to fork this workspace" preventBackdropClose={loading} customIcon={<GitBranch size={24} className="text-blue-500" />}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {t('fork.forking', { name: sourceWorkspace.name })}
        </p>

        <ForkSelectField
          label={t('fork.destination')}
          value={destination}
          onChange={setDestination}
          options={destinationOptions}
          helper={destinationHelper}
          aria-label={t('fork.forkDestination')}
        />

        {destination === 'local' && (
          <div className="space-y-3">
            <ForkSelectField
              label={t('fork.localTarget')}
              value={localTarget}
              onChange={setLocalTarget}
              options={localTargetOptions}
              aria-label={t('fork.localForkTarget')}
            />

            {localTarget === 'new' && (
              <ForkTextField
                label={t('fork.workspaceName')}
                type="text"
                value={newLocalName}
                onChange={(e) => setNewLocalName(e.target.value)}
                placeholder={t('fork.workspaceNamePlaceholder')}
                required
              />
            )}

            {localTarget === 'existing' && localWorkspaces.length > 0 && (
              <ForkSelectField
                label={t('fork.existingWorkspace')}
                value={String(existingLocalId)}
                onChange={setExistingLocalId}
                options={existingLocalOptions}
                aria-label={t('fork.existingLocalWorkspace')}
              />
            )}
          </div>
        )}

        <div className="flex items-start space-x-2 pt-1">
          <input
            type="checkbox"
            id="fork_copy_variables"
            checked={copyVariables}
            onChange={(e) => setCopyVariables(e.target.checked)}
            disabled={loading}
            className="checkbox-brand cursor-pointer mt-0.5"
          />
          <label
            htmlFor="fork_copy_variables"
            className="text-sm text-gray-700 dark:text-zinc-300 cursor-pointer"
          >
            {t('fork.copyVariables')}
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span>{loading ? t('fork.forkingBtn') : t('fork.forkBtn')}</span>
        </button>
      </form>
    </Modal>
  );
}
